/**
 * @file Axis-aligned bounding box maths on the XZ plane (GDD §12).
 *
 * The game is top-down, so height is irrelevant to collision: every box is
 * described by a centre (x, z) and half-extents (hx, hz). Every function here
 * is allocation-free — results are written into caller-owned objects.
 */

/**
 * @typedef {object} OverlapResult
 * @property {boolean} hit Whether the boxes intersect.
 * @property {number} normalX X component of the minimum translation axis.
 * @property {number} normalZ Z component of the minimum translation axis.
 * @property {number} depth Penetration depth along that axis.
 */

/**
 * @returns {OverlapResult} A reusable result object.
 */
export function createOverlapResult() {
  return { hit: false, normalX: 0, normalZ: 0, depth: 0 };
}

/**
 * Tests two boxes and, on a hit, computes the minimum translation vector.
 *
 * @param {number} ax Centre X of box A.
 * @param {number} az Centre Z of box A.
 * @param {number} ahx Half-extent X of box A.
 * @param {number} ahz Half-extent Z of box A.
 * @param {number} bx Centre X of box B.
 * @param {number} bz Centre Z of box B.
 * @param {number} bhx Half-extent X of box B.
 * @param {number} bhz Half-extent Z of box B.
 * @param {OverlapResult} out Result object, mutated in place.
 * @returns {OverlapResult} The same `out` object.
 */
export function overlapAABB(ax, az, ahx, ahz, bx, bz, bhx, bhz, out) {
  const dx = bx - ax;
  const overlapX = ahx + bhx - Math.abs(dx);
  if (overlapX <= 0) {
    out.hit = false;
    return out;
  }

  const dz = bz - az;
  const overlapZ = ahz + bhz - Math.abs(dz);
  if (overlapZ <= 0) {
    out.hit = false;
    return out;
  }

  out.hit = true;
  // Resolve along the shallower axis: that is the minimum translation vector.
  if (overlapX < overlapZ) {
    out.normalX = dx < 0 ? 1 : -1;
    out.normalZ = 0;
    out.depth = overlapX;
  } else {
    out.normalX = 0;
    out.normalZ = dz < 0 ? 1 : -1;
    out.depth = overlapZ;
  }
  return out;
}

/**
 * Slab test of a ray against a box, used for swept bullet collisions so a fast
 * projectile cannot tunnel through a thin wall in a single frame.
 *
 * @param {number} ox Ray origin X.
 * @param {number} oz Ray origin Z.
 * @param {number} dx Ray direction X (not required to be normalised).
 * @param {number} dz Ray direction Z.
 * @param {number} minX Box minimum X.
 * @param {number} minZ Box minimum Z.
 * @param {number} maxX Box maximum X.
 * @param {number} maxZ Box maximum Z.
 * @returns {number} Hit parameter in [0, 1] along `d`, or -1 when there is no hit.
 */
export function raycastAABB(ox, oz, dx, dz, minX, minZ, maxX, maxZ) {
  let tMin = 0;
  let tMax = 1;

  // X slab.
  if (Math.abs(dx) < 1e-8) {
    if (ox < minX || ox > maxX) return -1;
  } else {
    const inverse = 1 / dx;
    let t1 = (minX - ox) * inverse;
    let t2 = (maxX - ox) * inverse;
    if (t1 > t2) [t1, t2] = [t2, t1];
    tMin = Math.max(tMin, t1);
    tMax = Math.min(tMax, t2);
    if (tMin > tMax) return -1;
  }

  // Z slab.
  if (Math.abs(dz) < 1e-8) {
    if (oz < minZ || oz > maxZ) return -1;
  } else {
    const inverse = 1 / dz;
    let t1 = (minZ - oz) * inverse;
    let t2 = (maxZ - oz) * inverse;
    if (t1 > t2) [t1, t2] = [t2, t1];
    tMin = Math.max(tMin, t1);
    tMax = Math.min(tMax, t2);
    if (tMin > tMax) return -1;
  }

  return tMin;
}

/**
 * Face normal of the box at a point on its surface, used to orient impact FX.
 *
 * @param {number} px Point X.
 * @param {number} pz Point Z.
 * @param {number} cx Box centre X.
 * @param {number} cz Box centre Z.
 * @param {number} hx Half-extent X.
 * @param {number} hz Half-extent Z.
 * @param {{ x: number, z: number }} out Result, mutated in place.
 * @returns {{ x: number, z: number }} The same `out` object.
 */
export function faceNormal(px, pz, cx, cz, hx, hz, out) {
  const dx = (px - cx) / hx;
  const dz = (pz - cz) / hz;
  if (Math.abs(dx) > Math.abs(dz)) {
    out.x = Math.sign(dx) || 1;
    out.z = 0;
  } else {
    out.x = 0;
    out.z = Math.sign(dz) || 1;
  }
  return out;
}
